<div class="builder-step">
  <!-- ── Form column (own scroll) ─────────────────────────────────────── -->
  <div class="builder-step__form-scroll">
    <div class="builder-step__form">
      <!-- Rule name + description -->
      <div class="builder-step__intro">
        <ui-form-field
          type="text"
          [label]="'workflows.builder.rule_name' | translate"
          [placeholder]="'workflows.builder.rule_name_placeholder' | translate"
          [formControl]="nameControl"
        ></ui-form-field>

        <ui-form-field
          type="textarea"
          [label]="'workflows.builder.description' | translate"
          [placeholder]="'workflows.builder.description_placeholder' | translate"
          [formControl]="descriptionControl"
          [isOptional]="true"
        ></ui-form-field>
      </div>

      <!-- WHEN / IF / THEN timeline -->
      <div class="builder-step__steps">
        <!-- WHEN — Trigger -->
        <section class="builder-step__step">
          <div class="builder-step__rail">
            <span class="builder-step__badge">
              <ui-svg-icon svgIcon="bolt" size="medium"></ui-svg-icon>
            </span>
          </div>
          <div class="builder-step__step-body">
            <header class="builder-step__step-head">
              <span class="builder-step__step-label">{{ 'workflows.builder.when' | translate }}</span>
              <p class="builder-step__step-hint">{{ 'workflows.builder.trigger_hint' | translate }}</p>
            </header>
            <app-workflow-trigger-select
              [triggers]="schema.triggers"
              [selectedType]="rule.trigger?.type ?? null"
              [locked]="isTriggerLocked"
              (triggerChanged)="onTriggerChanged($event)"
            ></app-workflow-trigger-select>
          </div>
        </section>

        <!-- IF — Conditions -->
        <section class="builder-step__step">
          <div class="builder-step__rail">
            <span class="builder-step__badge">
              <ui-svg-icon svgIcon="condition" size="medium"></ui-svg-icon>
            </span>
          </div>
          <div class="builder-step__step-body">
            <header class="builder-step__step-head">
              <span class="builder-step__step-label">{{ 'workflows.builder.if' | translate }}</span>
              <p class="builder-step__step-hint">{{ 'workflows.builder.conditions_hint' | translate }}</p>
            </header>

            <div class="builder-step__condition-list" *ngIf="conditions.length">
              <app-workflow-condition-row
                *ngFor="let condition of conditions; trackBy: trackByConditionId"
                [condition]="condition"
                [conditionFields]="conditionFieldsFor(condition)"
                [enumCache]="enumCache"
                [locked]="isConditionLocked(condition)"
                (conditionChanged)="onConditionChanged($event)"
                (conditionRemoved)="onConditionRemoved($event)"
              ></app-workflow-condition-row>
            </div>

            <ui-button
              class="builder-step__add-button"
              type="transparent"
              [label]="'workflows.builder.add_condition' | translate"
              icon="plus-cc"
              iconSize="medium"
              [disabled]="!canAddCondition"
              (click)="addCondition()"
            ></ui-button>
          </div>
        </section>

        <!-- THEN — Actions -->
        <section class="builder-step__step">
          <div class="builder-step__rail">
            <span class="builder-step__badge">
              <ui-svg-icon svgIcon="action" size="medium"></ui-svg-icon>
            </span>
          </div>
          <div class="builder-step__step-body">
            <header class="builder-step__step-head">
              <span class="builder-step__step-label">{{ 'workflows.builder.then' | translate }}</span>
              <p class="builder-step__step-hint">{{ 'workflows.builder.actions_hint' | translate }}</p>
            </header>

            <div
              class="builder-step__action-list"
              cdkDropList
              [cdkDropListData]="actions"
              (cdkDropListDropped)="onActionDrop($event)"
            >
              <app-workflow-action-row
                *ngFor="let action of actions; trackBy: trackByActionId"
                cdkDrag
                cdkDragLockAxis="y"
                [action]="action"
                [actionDescriptors]="allowedActionDescriptors"
                [descriptorCatalog]="actionDescriptorCatalog"
                [enumCache]="enumCache"
                [locked]="isActionLocked(action)"
                [triggerType]="rule.trigger?.type ?? null"
                (actionChanged)="onActionChanged($event)"
                (actionRemoved)="onActionRemoved($event)"
              ></app-workflow-action-row>
            </div>

            <ui-button
              class="builder-step__add-button"
              type="transparent"
              [label]="'workflows.builder.add_action' | translate"
              icon="plus-cc"
              iconSize="medium"
              (click)="addAction()"
            ></ui-button>
          </div>
        </section>
      </div>

      <!-- Stop processing -->
      <div class="builder-step__stop-processing">
        <mat-slide-toggle
          [formControl]="stopProcessingControl"
          (change)="onStopProcessingChange()"
        ></mat-slide-toggle>
        <span class="builder-step__stop-text">
          <span class="builder-step__stop-label">{{ 'workflows.builder.stop_processing' | translate }}</span>
          <span class="builder-step__stop-hint">{{ 'workflows.builder.stop_processing_hint' | translate }}</span>
        </span>
      </div>
    </div>
  </div>

  <!-- ── Preview column (own background + scroll) ─────────────────────── -->
  <aside class="builder-step__preview">
    <span class="builder-step__preview-eyebrow">{{ 'workflows.builder.preview' | translate }}</span>

    <!-- Rule summary card -->
    <div class="builder-step__card">
      <div class="builder-step__card-head">
        <h3
          class="builder-step__card-title"
          [class.builder-step__card-title--empty]="!rule.name"
        >
          {{ rule.name || ('workflows.builder.preview_untitled' | translate) }}
        </h3>
        <span class="builder-step__status" [ngClass]="'builder-step__status--' + currentStatus">
          <span class="builder-step__status-dot"></span>
          {{ 'workflows.builder.status.' + currentStatus | translate }}
        </span>
      </div>

      <div class="builder-step__card-body" *ngIf="hasPreviewContent; else previewEmpty">
        <!-- When -->
        <div class="builder-step__rule-row" *ngIf="previewTrigger as trigger">
          <span class="builder-step__rule-key">{{ 'workflows.builder.when' | translate }}</span>
          <div class="builder-step__chips">
            <span class="builder-step__chip builder-step__chip--trigger">
              {{ trigger.label }}
            </span>
          </div>
        </div>

        <!-- If -->
        <div class="builder-step__rule-row" *ngIf="previewConditions.length">
          <span class="builder-step__rule-key">{{ 'workflows.builder.if' | translate }}</span>
          <div class="builder-step__chips">
            <ng-container *ngFor="let line of previewConditions; let last = last">
              <span class="builder-step__chip">
                <span>{{ line.field }} {{ line.operator }} <b>{{ line.value }}</b></span>
              </span>
              <span class="builder-step__chip-sep" *ngIf="!last">
                {{ 'workflows.builder.and' | translate }}
              </span>
            </ng-container>
          </div>
        </div>

        <!-- Then -->
        <div class="builder-step__rule-row" *ngIf="previewActions.length">
          <span class="builder-step__rule-key">{{ 'workflows.builder.then' | translate }}</span>
          <ol class="builder-step__action-steps">
            <li
              class="builder-step__action-step"
              *ngFor="let line of previewActions; let i = index"
            >
              <span class="builder-step__action-index">{{ i + 1 }}</span>
              <span class="builder-step__action-label">{{ line }}</span>
            </li>
          </ol>
        </div>
      </div>

      <ng-template #previewEmpty>
        <p class="builder-step__preview-placeholder">{{ 'workflows.builder.preview_hint' | translate }}</p>
      </ng-template>

      <p class="builder-step__preview-note" *ngIf="isInactive">
        {{ 'workflows.builder.preview_inactive_note' | translate }}
      </p>
    </div>

    <!-- Last runs (edit-only). Data is placeholder; history fetch is a separate task. -->
    <section class="builder-step__runs" *ngIf="isEditMode">
      <header class="builder-step__runs-head">
        <span class="builder-step__runs-title">{{ 'workflows.builder.preview_last_runs' | translate }}</span>
        <span class="builder-step__runs-window" *ngIf="lastRunsCount">
          {{ 'workflows.builder.preview_runs_window' | translate: { count: lastRunsCount } }}
        </span>
      </header>

      <p class="builder-step__runs-empty" *ngIf="lastRunsLoading">
        {{ 'workflows.history.loading' | translate }}
      </p>

      <ul class="builder-step__runs-list" *ngIf="!lastRunsLoading && lastRuns.length">
        <li class="builder-step__run" *ngFor="let run of lastRuns; trackBy: trackByRunId">
          <span
            class="builder-step__run-dot"
            [ngClass]="'builder-step__run-dot--' + run.status"
            [title]="runStatusLabel(run.status)"
          ></span>
          <span class="builder-step__run-text">
            <span class="builder-step__run-label">{{ run.thread?.subject || run.ruleName }}</span>
            <span class="builder-step__run-time">
              {{ run.executedAt | daysAgoOrLeft: 'ago' : 2 : 'DD MMM · HH:mm' }}
            </span>
          </span>
          <a
            class="builder-step__run-ref"
            *ngIf="runTargetUrl(run) as targetUrl"
            [href]="targetUrl"
            target="_blank"
            rel="noopener noreferrer"
          >{{ runTargetRef(run) }}</a>
        </li>
      </ul>

      <p class="builder-step__runs-empty" *ngIf="!lastRunsLoading && !lastRuns.length">
        {{ 'workflows.builder.preview_no_runs' | translate }}
      </p>
    </section>
  </aside>
</div>
